<template>
  <main class="main">
    <portal to="headerTitle">
      {{ $t('admin.pageInvoiceSettings.card.title') }}
    </portal>
    <template v-if="!isNil(hotel)">
      <card v-if="hasLicense('Stationery') || hasLicense('StationeryFirstPage')" :title="$t('admin.pageInvoiceSettings.card.stationery')" cols="12 6@desktop" rows="2">
        <hm-input-file v-model="hotel.stationery" :url="serviceUrls.hotelDetails.uploadFile(this, 'stationery')" :label="$t('admin.pageInvoiceSettings.stationery')" cols="12" />
        <hm-input-file v-if="hasLicense('StationeryFirstPage')"
                       v-model="hotel.stationeryFirstPage"
                       :url="serviceUrls.hotelDetails.uploadFile(this, 'stationeryFirstPage')"
                       :label="$t('admin.pageInvoiceSettings.stationeryFirstPage')"
                       cols="12" />
        <hm-input-number v-model="hotel.stationeryHeaderMargin"
                         :max-fraction-digits="0"
                         :label="$t('admin.pageInvoiceSettings.stationeryHeaderMargin')"
                         :help="$t('admin.pageInvoiceSettings.stationeryHeaderMargin_help')"
                         cols="12" />
        <hm-input-number v-model="hotel.stationeryFooterMargin"
                         :max-fraction-digits="0"
                         :label="$t('admin.pageInvoiceSettings.stationeryFooterMargin')"
                         :help="$t('admin.pageInvoiceSettings.stationeryFooterMargin_help')"
                         cols="12" />
        <hm-input-number v-model="hotel.stationeryLeftMargin"
                         :max-fraction-digits="0"
                         :label="$t('admin.pageInvoiceSettings.stationeryLeftMargin')"
                         :help="$t('admin.pageInvoiceSettings.stationeryLeftMargin_help')"
                         cols="12" />
        <hm-input-number v-model="hotel.stationeryRightMargin"
                         :max-fraction-digits="0"
                         :label="$t('admin.pageInvoiceSettings.stationeryRightMargin')"
                         :help="$t('admin.pageInvoiceSettings.stationeryRightMargin_help')"
                         cols="12" />
      </card>
      <card :title="$t('admin.pageInvoiceSettings.card.invoiceNumber')" cols="12 6@desktop">
        <hm-input :lazy="true" v-model="hotel.invoiceNumberPrefix" :label="$t('admin.pageInvoiceSettings.invoiceNumberPrefix')" />
        <hm-input-number :lazy="true" v-model="hotel.initialInvoiceNumber" :label="$t('admin.pageInvoiceSettings.initialInvoiceNumber')" :help="$t('admin.pageInvoiceSettings.initialInvoiceNumber_help')" />
        <hm-input :disabled="true" :value="`${isEmpty(hotel.invoiceNumberPrefix) ? '' : hotel.invoiceNumberPrefix}${$moment().year()}-00001`" :label="$t('admin.pageInvoiceSettings.invoiceNumberExample')" />
      </card>
      <card :title="$t('admin.pageInvoiceSettings.card.additionalSettings')" cols="12 6@desktop start-1">
        <hm-input-select v-model="hotel.defaultInvoiceDocumentType" :label="$t('admin.pageInvoiceSettings.defaultInvoiceDocumentType')">
          <option v-for="item in enums['InvoiceMediaType']" v-if="item !== 'Both'" :value="item" v-html="`${$t(`InvoiceMediaType.${item}`)}`"></option>
        </hm-input-select>
        <hm-input-select v-model="hotel.documentLayout" :label="$t('admin.pageInvoiceSettings.documentLayout')" enum-name="DocumentLayout" />
      </card>
      <card :title="$t('admin.pageInvoiceSettings.card.invoice')" cols="12 6@desktop start-7@desktop" rows="4 start-1@desktop">
        <hm-card-inner :title="$t('admin.pageInvoiceSettings.card.invoiceAdditionalInformation')">
          <hm-input :lazy="true" v-model="hotel.stationeryEnvelopeWindowSender" :label="$t('admin.pageInvoiceSettings.stationeryEnvelopeWindowSender')" :help="$t('admin.pageInvoiceSettings.stationeryEnvelopeWindowSender_help')" />
          <hm-textarea :lazy="true"
                       v-model="hotel.invoiceTextTop"
                       :input-rows="2"
                       :label="$t('admin.pageInvoiceSettings.invoiceTextTop')"
                       :help="$t('admin.pageInvoiceSettings.invoiceTextTop_help')" />
          <hm-textarea :lazy="true"
                       v-model="hotel.invoiceTextBottom"
                       :input-rows="2"
                       :label="$t('admin.pageInvoiceSettings.invoiceTextBottom')"
                       :help="$t('admin.pageInvoiceSettings.invoiceTextBottom_help')" />
          <hm-textarea :lazy="true"
                       v-model="hotel.invoiceFooterLineAddition"
                       :disabled="!isEmpty(hotel.stationery) && !isEmpty(hotel.stationery.fileName)"
                       :input-rows="2"
                       :label="$t('admin.pageInvoiceSettings.invoiceFooterLineAddition')"
                       :help="$t('admin.pageInvoiceSettings.invoiceFooterLineAddition_help')" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageInvoiceSettings.card.invoiceFormattingOptions')">
          <hm-input-select v-model="hotel.reservationDetailsOnInvoice" :label="$t('admin.pageInvoiceSettings.reservationDetailsOnInvoice')" :help="$t('admin.pageInvoiceSettings.reservationDetailsOnInvoice_help')" enum-name="ReservationDetailsOnInvoice" />
          <hm-input-toggle v-model="hotel.invoiceCondensed" :label="$t('admin.pageInvoiceSettings.invoiceCondensed')" />
          <hm-input-toggle v-model="hotel.invoiceHideChildTransactionLines" :label="$t('admin.pageInvoiceSettings.invoiceHideChildTransactionLines')" />
          <hm-input-toggle v-model="hotel.invoiceHideVatLabel" :label="$t('admin.pageInvoiceSettings.invoiceHideVatLabel')" />
          <hm-input-toggle v-model="hotel.displayPaymentMethodOnInvoice" :label="$t('admin.pageInvoiceSettings.displayPaymentMethodOnInvoice')" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.pageInvoiceSettings.card.draftInvoice')" cols="12 6@desktop">
        <hm-input-multilang v-if="hasLicense('CustomProformaInvoice')"
                            v-model="hotel.proformaInvoiceTitle"
                            :label="$t('admin.pageInvoiceSettings.proformaInvoiceTitle')"
                            :help="$t('admin.pageInvoiceSettings.proformaInvoiceTitle_help')"/>
        <hm-input-toggle v-model="hotel.hideInvoiceNumberOnProforma" :label="$t('admin.pageInvoiceSettings.hideInvoiceNumberOnProforma')" :help="$t('admin.pageInvoiceSettings.hideInvoiceNumberOnProforma_help')" />
      </card>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmTextarea from '../../lib/inputs/HmTextarea';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmInputToggle from '../../lib/inputs/HmInputToggle';
import HmInputSelect from '../../lib/inputs/HmInputSelect';

export default {
  name: 'PageInvoiceSettings',
  components: { HmInputSelect, HmInputToggle, HmCardInner, HmTextarea },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'hotel',
      variable: 'hotel',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        this.axios.put(this.serviceUrls.hotelDetails.invoice(this), this.hotel)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      hotel: null,
    };
  },

  computed: {
    allLanguages: function () {
      const ret = [this.hotelSettings.details.primaryLanguage];

      this.hotelSettings.details.additionalLanguages.forEach(it => ret.push(it));

      return ret;
    },
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.hotelDetails.invoice(this))
        .then((response) => {
          this.hotel = response.data.data;
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageInvoiceSettings.vue